import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { listProducts } from '@paulownia/shared/services/products';
import { refreshProducts } from '@paulownia/shared/services/products-cache';
import { getTenantById, getTenantBySlug } from '@paulownia/shared/services/tenant';
import { getTenantMarketsWithDetails } from '@paulownia/shared/services/markets';
import { requestNotificationPermissionWithDetails } from '@paulownia/shared/services/notifications';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { initFirebase } from '@paulownia/shared/lib/firebase';
import { getFavorites, addFavorite, removeFavorite, saveLocalOrder, saveLastMarket } from '@paulownia/shared/lib/storage';
import { getCustomerIdAsync } from '@/lib/customer-auth';
import { toast } from 'sonner';
import { type Product, type OrderLineOption, type Market, type TenantMarket, type SaleMode, isProductActive, isProductAvailableAtMarket, getEffectiveSaleMode, isUnitCountSaleMode, isTenantAbsent, getOrderDeadline, isOrderingOpen, formatOrderDeadline } from '@paulownia/shared/types';
import { getTodayKey, getNextDayOfWeek, isDateInAbsencePeriod, getNextDayOfWeekAfterAbsence } from '@paulownia/shared/lib/date-utils';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { PullToRefresh } from '@/components/PullToRefresh';
import { useCurrentMarket } from '@/contexts/CurrentMarketContext';
import { useMerchantCarts } from '@/contexts/CartContext';
import { OrderPageSkeleton } from './components/OrderPageSkeleton';
import { TenantNotFound } from './components/TenantNotFound';
import { MerchantAbsent } from './components/MerchantAbsent';
import { OrderTracking } from './components/OrderTracking';
import { TenantHeader } from './components/TenantHeader';
import { SectionTabs } from './components/SectionTabs';
import { SubSectionTabs } from './components/SubSectionTabs';
import { ProductRowCard } from './components/ProductRowCard';
import { ProductAddModal } from './components/ProductAddModal';
import { FloatingCartButton } from './components/FloatingCartButton';
import { CartModal } from './components/CartModal';
import { CheckoutModal } from './components/CheckoutModal';
import { MarketSelectionGate } from './components/MarketSelectionGate';
import { MarketIncompatible } from './components/MarketIncompatible';
import { CurrentMarketBanner } from './components/CurrentMarketBanner';
import { MarketChangeModal } from './components/MarketChangeModal';
import type { CartItem, BeforeInstallPromptEvent, TimeSlot } from './components/types';
import { calculateEstimatedWeight, calculateEstimatedPrice } from './components/sale-mode-utils';
import { reportError } from '@paulownia/shared/lib/error-reporting';

// Calculate total price modifier from selected options in a cart item
function calculateCartItemOptionsModifier(item: CartItem, product: Product | undefined): number {
    if (!item.options || !product?.availableOptions) return 0;

    let total = 0;

    for (const itemOption of item.options) {
        const productOption = product.availableOptions.find(o => o.id === itemOption.id);
        if (!productOption || !productOption.choices) continue;

        if (productOption.type === 'select' && typeof itemOption.value === 'string') {
            const choice = productOption.choices.find(c => c.id === itemOption.value);
            if (choice?.priceModifier) {
                total += choice.priceModifier;
            }
        } else if (productOption.type === 'multiselect' && Array.isArray(itemOption.value)) {
            for (const choiceId of itemOption.value) {
                if (typeof choiceId === 'string') {
                    const choice = productOption.choices.find(c => c.id === choiceId);
                    if (choice?.priceModifier) {
                        total += choice.priceModifier;
                    }
                }
            }
        }
    }

    return total;
}

export function CustomerOrderPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const params = useParams<{ slug?: string }>();
    const marketFromUrl = searchParams.get('market');

    // Global market & cart contexts
    const { currentMarket, isLoading: marketContextLoading, setMarket: setGlobalMarket } = useCurrentMarket();
    const { getCartForMerchant, setCartItems, clearCartForMerchant } = useMerchantCarts();

    // Tenant state
    const [tenantId, setTenantId] = useState<string | null>(null);
    const [tenantName, setTenantName] = useState<string>('');
    const [tenantPhoneNumber, setTenantPhoneNumber] = useState<string | undefined>(undefined);
    const [tenantNotFound, setTenantNotFound] = useState(false);
    const [tenantAbsent, setTenantAbsent] = useState(false);
    const [tenantAbsenceStart, setTenantAbsenceStart] = useState<string | undefined>(undefined);
    const [tenantAbsenceEnd, setTenantAbsenceEnd] = useState<string | undefined>(undefined);
    const [tenantLoading, setTenantLoading] = useState(true);
    const [tenantSettings, setTenantSettings] = useState<{
        sectionOrder?: Record<string, number>;
        subSectionOrder?: Record<string, Record<string, number>>;
    } | null>(null);

    // Form state
    const [customerName, setCustomerName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [note, setNote] = useState('');
    const [pickupTime, setPickupTime] = useState('');
    const [wantsNotifications, setWantsNotifications] = useState(false);

    // Market selection state
    const [tenantMarkets, setTenantMarkets] = useState<(TenantMarket & { market: Market })[]>([]);
    const [selectedMarketId, setSelectedMarketId] = useState<string | null>(null);
    const [marketsLoading, setMarketsLoading] = useState(true);

    // PWA state
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [isInstallable, setIsInstallable] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);

    // Products and cart state
    const [products, setProducts] = useState<Product[]>([]);
    const [productsLoading, setProductsLoading] = useState(true);
    const [cart, setCart] = useState<CartItem[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [orderPlaced, setOrderPlaced] = useState(false);
    const [placedOrderId, setPlacedOrderId] = useState<string | null>(null);
    const [activeSection, setActiveSection] = useState<string>('');
    const [activeSubSection, setActiveSubSection] = useState<string | null>(null);

    // Modal state
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [isMarketChangeModalOpen, setIsMarketChangeModalOpen] = useState(false);
    const [showMarketSelector, setShowMarketSelector] = useState(false);

    // Refs for scrolling to sections
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const subSectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const tabsContainerRef = useRef<HTMLDivElement>(null);
    const productRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const isManualScrollingRef = useRef(false);
    const hasInitialSectionBeenSetRef = useRef(false);
    // Track all currently visible sections (not just the ones that changed)
    const visibleSectionsRef = useRef<Set<string>>(new Set());

    // ==================== Effects ====================

    // Reset all tenant-specific state when slug changes to prevent stale data flash
    useEffect(() => {
        // Clear all tenant-specific data immediately when switching merchants
        // Also reset tenant identity to prevent loading products for old tenant
        setTenantId(null);
        setTenantName('');
        setTenantLoading(true);
        setTenantAbsent(false);
        setTenantAbsenceStart(undefined);
        setTenantAbsenceEnd(undefined);
        setProducts([]);
        setProductsLoading(true);
        setCart([]);
        setTenantMarkets([]);
        setTenantSettings(null);
        setActiveSection('');
        setActiveSubSection(null);
        setSelectedMarketId(null);
        setOrderPlaced(false);
        setPlacedOrderId(null);
        setShowMarketSelector(false);
        hasInitialSectionBeenSetRef.current = false;
    }, [params.slug]);

    // Load tenant from URL
    useEffect(() => {
        const loadTenant = async () => {
            setTenantLoading(true);
            setTenantNotFound(false);

            if (params.slug) {
                const currentSlug = params.slug;
                try {
                    const tenant = await getTenantBySlug(currentSlug);
                    if (tenant) {
                        import('@paulownia/shared/lib/storage').then(({ saveTenantSlug }) => {
                            saveTenantSlug(currentSlug);
                        });
                        setTenantId(tenant.id);
                        setTenantName(tenant.name);
                        setTenantPhoneNumber(tenant.phoneNumber);
                        setTenantNotFound(false);
                        // Check if tenant is absent today
                        const isAbsent = isTenantAbsent(tenant);
                        setTenantAbsent(isAbsent);
                        // Store absence period even if not currently absent (for pickup date filtering)
                        setTenantAbsenceStart(tenant.absenceStart);
                        setTenantAbsenceEnd(tenant.absenceEnd);
                    } else {
                        setTenantNotFound(true);
                        setTenantId(null);
                        setTenantName('');
                    }
                } catch (error) {
                    console.error('[CustomerOrderPage] Failed to load tenant by slug:', currentSlug, error);
                    setTenantNotFound(true);
                    setTenantId(null);
                    setTenantName('');
                } finally {
                    setTenantLoading(false);
                }
                return;
            }

            const tenantParam = searchParams.get('tenant');
            if (tenantParam) {
                setTenantId(tenantParam);
                try {
                    const tenant = await getTenantById(tenantParam);
                    if (tenant) {
                        setTenantName(tenant.name);
                        setTenantPhoneNumber(tenant.phoneNumber);
                    }
                } catch (error) {
                    reportError(error, { context: 'CustomerOrderPage.loadTenantByParam', tenantParam });
                }
                setTenantLoading(false);
                return;
            }

            setTenantLoading(false);
            setTenantId(null);
            setTenantName('');
        };

        loadTenant();
    }, [params.slug, searchParams]);

    // Load products - only when we have a valid tenantId
    useEffect(() => {
        // Don't load products without a tenantId to prevent loading stale cached data
        if (!tenantId) {
            setProductsLoading(false);
            return;
        }
        setProductsLoading(true);
        listProducts(tenantId)
            .then(setProducts)
            .catch(() => {
                toast.error('Impossible de charger le menu');
            })
            .finally(() => {
                setProductsLoading(false);
            });
    }, [tenantId]);

    // Load tenant settings
    useEffect(() => {
        if (!tenantId) {
            setTenantSettings(null);
            return;
        }

        getTenantById(tenantId)
            .then((tenant) => {
                if (tenant) {
                    setTenantSettings(tenant.settings || null);
                }
            })
            .catch((error) => {
                reportError(error, { context: 'CustomerOrderPage.loadTenantSettings', tenantId });
            });
    }, [tenantId]);

    // Load markets for this tenant (only depends on tenantId, not currentMarket)
    useEffect(() => {
        if (!tenantId) {
            setTenantMarkets([]);
            setSelectedMarketId(null);
            setMarketsLoading(false);
            return;
        }

        setMarketsLoading(true);
        getTenantMarketsWithDetails(tenantId)
            .then((markets) => {
                // Sort by dayOfWeek (Monday=1 to Sunday=7)
                const sorted = [...markets].sort((a, b) => a.market.dayOfWeek - b.market.dayOfWeek);
                setTenantMarkets(sorted);
            })
            .catch((error) => {
                reportError(error, { context: 'CustomerOrderPage.loadMarkets', tenantId });
                setTenantMarkets([]);
            })
            .finally(() => {
                setMarketsLoading(false);
            });
    }, [tenantId]);

    // Select the initial market based on URL param or global context
    useEffect(() => {
        if (marketsLoading || tenantMarkets.length === 0) return;

        // Priority 1: URL query param (from market page)
        const marketFromUrl = searchParams.get('market');
        if (marketFromUrl && tenantMarkets.some(m => m.marketId === marketFromUrl)) {
            setSelectedMarketId(marketFromUrl);
            // Also set as global market, but only if different to avoid infinite loop
            // (setGlobalMarket creates a new object reference which would re-trigger this effect)
            const tm = tenantMarkets.find(m => m.marketId === marketFromUrl);
            if (tm && currentMarket?.marketId !== tm.marketId) {
                setGlobalMarket({
                    marketId: tm.marketId,
                    city: tm.market.city,
                    dayOfWeek: tm.market.dayOfWeek,
                });
            }
            return;
        }
        // Priority 2: Use global currentMarket if this merchant is present there
        if (currentMarket && tenantMarkets.some(m => m.marketId === currentMarket.marketId)) {
            setSelectedMarketId(currentMarket.marketId);
            return;
        }
        // Priority 3: No match - will show gate or incompatibility screen
        setSelectedMarketId(null);
    }, [tenantMarkets, searchParams, currentMarket, setGlobalMarket]);

    // Initialize cart from context when tenant loads
    useEffect(() => {
        if (!tenantId) return;
        const storedCart = getCartForMerchant(tenantId);
        if (storedCart && storedCart.items.length > 0) {
            // Restore cart items (cast from StoredCartItem to CartItem)
            setCart(storedCart.items as unknown as CartItem[]);
        }
    }, [tenantId, getCartForMerchant]);

    // Sync local cart changes to context
    useEffect(() => {
        if (!tenantId || !params.slug || !selectedMarketId || !currentMarket) return;
        if (cart.length === 0) {
            clearCartForMerchant(tenantId);
        } else {
            setCartItems(
                tenantId,
                params.slug,
                tenantName,
                selectedMarketId,
                currentMarket.city,
                currentMarket.dayOfWeek,
                cart as unknown as import('@paulownia/shared/lib/storage').StoredCartItem[]
            );
        }
    }, [cart, tenantId, params.slug, tenantName, selectedMarketId, currentMarket, setCartItems, clearCartForMerchant]);

    // Check favorites
    useEffect(() => {
        if (params.slug) {
            getFavorites().then((favorites) => {
                setIsFavorite(favorites.includes(params.slug!));
            }).catch((error) => {
                reportError(error, { context: 'CustomerOrderPage.loadFavorites', slug: params.slug });
            });
        }
    }, [params.slug]);

    // Computed values needed by effects below
    const productsBySection = useMemo(() => {
        const grouped: Record<string, Product[]> = {};

        // Filter out inactive products and products not available at selected market
        const activeProducts = products
            .filter(isProductActive)
            .filter(p => isProductAvailableAtMarket(p, selectedMarketId));

        activeProducts.forEach((product) => {
            const section = product.section || 'Autres';
            if (!grouped[section]) grouped[section] = [];
            grouped[section].push(product);
        });

        Object.keys(grouped).forEach((section) => {
            grouped[section].sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
        });

        return grouped;
    }, [products, selectedMarketId]);

    const sortedSections = useMemo(() => {
        const sections = Object.keys(productsBySection);
        if (tenantSettings?.sectionOrder) {
            return sections.sort((a, b) => {
                const orderA = tenantSettings.sectionOrder![a] ?? 999;
                const orderB = tenantSettings.sectionOrder![b] ?? 999;
                return orderA - orderB;
            });
        }
        return sections.sort();
    }, [productsBySection, tenantSettings]);

    // Get subSections grouped by section
    const subSectionsBySection = useMemo(() => {
        const result: Record<string, string[]> = {};
        // Only consider active products available at selected market for subsection tabs
        const activeProducts = products
            .filter(isProductActive)
            .filter(p => isProductAvailableAtMarket(p, selectedMarketId));
        activeProducts.forEach((p) => {
            const section = p.section || 'Autres';
            if (p.subSection) {
                if (!result[section]) result[section] = [];
                if (!result[section].includes(p.subSection)) {
                    result[section].push(p.subSection);
                }
            }
        });
        // Sort subSections using tenant settings or alphabetically
        Object.keys(result).forEach((section) => {
            if (tenantSettings?.subSectionOrder?.[section]) {
                result[section].sort((a, b) => {
                    const orderA = tenantSettings.subSectionOrder![section][a] ?? 999;
                    const orderB = tenantSettings.subSectionOrder![section][b] ?? 999;
                    return orderA - orderB;
                });
            } else {
                result[section].sort();
            }
        });
        return result;
    }, [products, selectedMarketId, tenantSettings]);

    // Get subSections for the active section
    const currentSubSections = useMemo(() => {
        return subSectionsBySection[activeSection] || [];
    }, [subSectionsBySection, activeSection]);

    // Group products by subsection within each section for display
    const productsBySubSection = useMemo(() => {
        const result: Record<string, { subSection: string | null; products: Product[] }[]> = {};

        Object.entries(productsBySection).forEach(([section, sectionProducts]) => {
            const subSections = subSectionsBySection[section] || [];

            if (subSections.length === 0) {
                // No subsections, just return all products in one group
                result[section] = [{ subSection: null, products: sectionProducts }];
            } else {
                // Group products by subsection
                const groups: { subSection: string | null; products: Product[] }[] = [];

                // First, products without subsection
                const productsWithoutSubSection = sectionProducts.filter(p => !p.subSection);
                if (productsWithoutSubSection.length > 0) {
                    groups.push({ subSection: null, products: productsWithoutSubSection });
                }

                // Then, products grouped by subsection (in order)
                subSections.forEach(subSection => {
                    const subSectionProducts = sectionProducts.filter(p => p.subSection === subSection);
                    if (subSectionProducts.length > 0) {
                        groups.push({ subSection, products: subSectionProducts });
                    }
                });

                result[section] = groups;
            }
        });

        return result;
    }, [productsBySection, subSectionsBySection]);


    // Set initial active section
    useEffect(() => {
        if (sortedSections.length > 0 && !activeSection) {
            setActiveSection(sortedSections[0]);
            hasInitialSectionBeenSetRef.current = true;
        }
    }, [sortedSections, activeSection]);

    // Reset activeSubSection when activeSection changes
    useEffect(() => {
        setActiveSubSection(null);
    }, [activeSection]);

    // Intersection Observer for section scrolling
    // Tracks ALL currently visible sections to handle small sections correctly.
    // The observer callback only receives entries that changed, so we maintain
    // a persistent set of visible sections to always select the correct topmost one.
    useEffect(() => {
        // Reset visible sections when products change
        visibleSectionsRef.current = new Set();

        const observer = new IntersectionObserver(
            (entries) => {
                // Ignore observer updates during manual scrolling to prevent race condition
                if (isManualScrollingRef.current) return;

                // Ignore observer updates until initial section has been set
                if (!hasInitialSectionBeenSetRef.current) return;

                // Update the set of visible sections based on this callback
                entries.forEach(entry => {
                    const section = Object.keys(sectionRefs.current).find(
                        key => sectionRefs.current[key] === entry.target
                    );
                    if (section) {
                        if (entry.isIntersecting) {
                            visibleSectionsRef.current.add(section);
                        } else {
                            visibleSectionsRef.current.delete(section);
                        }
                    }
                });

                // No visible sections - nothing to update
                if (visibleSectionsRef.current.size === 0) return;

                // Find the topmost visible section by checking their current DOM positions
                let topmostSection: string | null = null;
                let topmostTop = Infinity;

                visibleSectionsRef.current.forEach(section => {
                    const ref = sectionRefs.current[section];
                    if (ref) {
                        const rect = ref.getBoundingClientRect();
                        if (rect.top < topmostTop) {
                            topmostTop = rect.top;
                            topmostSection = section;
                        }
                    }
                });

                if (topmostSection) {
                    setActiveSection(topmostSection);
                    const activeTab = document.querySelector(`[data-section="${topmostSection}"]`);
                    if (activeTab && tabsContainerRef.current) {
                        activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                    }
                }
            },
            { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
        );

        Object.values(sectionRefs.current).forEach(ref => {
            if (ref) observer.observe(ref);
        });

        return () => observer.disconnect();
    }, [products, sortedSections]);

    // PWA install prompt
    useEffect(() => {
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            setIsInstallable(true);
        };

        const handleAppInstalled = () => {
            setDeferredPrompt(null);
            setIsInstallable(false);
            toast.success('Application installée avec succès !');
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        if (window.matchMedia('(display-mode: standalone)').matches) {
            setIsInstallable(false);
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);


    // ==================== Computed values ====================

    const selectedMarket = useMemo(() => {
        return tenantMarkets.find(tm => tm.marketId === selectedMarketId) || null;
    }, [tenantMarkets, selectedMarketId]);

    const pickupDate = useMemo(() => {
        if (!selectedMarket) return getTodayKey();
        // Cast to number to handle potential legacy data from Firestore cache
        let dayOfWeek = selectedMarket.market.dayOfWeek as number;
        // Handle legacy format where Sunday was 0 (now should be 7 per ISO 8601)
        if (dayOfWeek === 0) {
            console.warn('Legacy dayOfWeek 0 detected, converting to 7 (Sunday)');
            dayOfWeek = 7;
        }
        // Validate dayOfWeek before calculation
        if (typeof dayOfWeek !== 'number' || dayOfWeek < 1 || dayOfWeek > 7) {
            console.error('Invalid dayOfWeek for market:', { dayOfWeek, marketId: selectedMarket.marketId });
            return getTodayKey();
        }

        // Calculate the natural next date for this market
        const naturalDate = getNextDayOfWeek(dayOfWeek);

        // If the deadline for this date has already passed, skip to next week
        if (!isOrderingOpen(selectedMarket.market, selectedMarket, naturalDate)) {
            const nextWeek = new Date();
            nextWeek.setDate(nextWeek.getDate() + 7);
            const nextWeekDate = getNextDayOfWeek(dayOfWeek, nextWeek);

            // Check absence for the next week date too
            if (tenantAbsenceEnd && isDateInAbsencePeriod(nextWeekDate, tenantAbsenceStart, tenantAbsenceEnd)) {
                return getNextDayOfWeekAfterAbsence(dayOfWeek, tenantAbsenceEnd);
            }
            return nextWeekDate;
        }

        // If the natural date falls within the absence period, skip to after absence
        if (tenantAbsenceEnd && isDateInAbsencePeriod(naturalDate, tenantAbsenceStart, tenantAbsenceEnd)) {
            return getNextDayOfWeekAfterAbsence(dayOfWeek, tenantAbsenceEnd);
        }

        return naturalDate;
    }, [selectedMarket, tenantAbsenceStart, tenantAbsenceEnd]);

    const formattedPickupDate = useMemo(() => {
        if (!pickupDate) return '';
        // Validate date format (YYYY-MM-DD)
        if (!/^\d{4}-\d{2}-\d{2}$/.test(pickupDate)) {
            console.error('Invalid pickupDate format:', pickupDate);
            return '';
        }
        const [year, month, day] = pickupDate.split('-').map(Number);
        // Validate parsed values
        if (isNaN(year) || isNaN(month) || isNaN(day)) {
            console.error('Invalid date components:', { year, month, day, pickupDate });
            return '';
        }
        const date = new Date(year, month - 1, day);
        // Check for invalid date
        if (isNaN(date.getTime())) {
            console.error('Invalid date created:', { year, month, day });
            return '';
        }
        return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    }, [pickupDate]);

    // Check if the natural pickup date was skipped due to merchant absence
    const absenceSkipInfo = useMemo(() => {
        if (!selectedMarket || !tenantAbsenceStart || !tenantAbsenceEnd) return null;

        let dayOfWeek = selectedMarket.market.dayOfWeek as number;
        if (dayOfWeek === 0) dayOfWeek = 7;
        if (typeof dayOfWeek !== 'number' || dayOfWeek < 1 || dayOfWeek > 7) return null;

        const naturalDate = getNextDayOfWeek(dayOfWeek);

        // If the natural date falls within absence, we skipped to a later date
        if (isDateInAbsencePeriod(naturalDate, tenantAbsenceStart, tenantAbsenceEnd)) {
            return {
                originalDate: naturalDate,
                absenceEnd: tenantAbsenceEnd,
                skippedToDate: pickupDate,
            };
        }

        return null;
    }, [selectedMarket, tenantAbsenceStart, tenantAbsenceEnd, pickupDate]);

    // Check if the natural pickup date was skipped due to order closure (deadline passed)
    const orderClosureSkipInfo = useMemo(() => {
        if (!selectedMarket) return null;

        let dayOfWeek = selectedMarket.market.dayOfWeek as number;
        if (dayOfWeek === 0) dayOfWeek = 7;
        if (typeof dayOfWeek !== 'number' || dayOfWeek < 1 || dayOfWeek > 7) return null;

        const naturalDate = getNextDayOfWeek(dayOfWeek);

        // Only applies when the deadline for the natural date has passed
        if (isOrderingOpen(selectedMarket.market, selectedMarket, naturalDate)) return null;

        // The natural date is closed. Check that the pickup date was actually skipped forward.
        if (pickupDate === naturalDate) return null;

        // Don't show if the skip was due to absence (absenceSkipInfo handles that)
        if (absenceSkipInfo) return null;

        // Compute deadline for the natural date
        const deadline = getOrderDeadline(selectedMarket.market, selectedMarket, naturalDate);
        if (!deadline) return null;

        const formatDate = (dateStr: string) => {
            const [y, m, d] = dateStr.split('-').map(Number);
            const date = new Date(y, m - 1, d);
            return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
        };

        return {
            originalDate: naturalDate,
            skippedToDate: pickupDate,
            deadline,
            formattedDeadline: formatOrderDeadline(deadline),
            formattedOriginalDate: formatDate(naturalDate),
            formattedSkippedToDate: formatDate(pickupDate),
        };
    }, [selectedMarket, pickupDate, absenceSkipInfo]);

    const timeSlots = useMemo<TimeSlot[]>(() => {
        if (!selectedMarket) return [];

        const slots: TimeSlot[] = [];
        const now = new Date();
        const isToday = pickupDate === getTodayKey();

        // Use tenant-specific order times if set, otherwise fall back to market hours
        const startTime = selectedMarket.orderStartTime || selectedMarket.market.openingTime;
        const endTime = selectedMarket.orderEndTime || selectedMarket.market.closingTime;

        // Defensive check for invalid market data
        if (!startTime || !endTime || typeof startTime !== 'string' || typeof endTime !== 'string') {
            console.error('Invalid market data:', { startTime, endTime, marketId: selectedMarket.marketId });
            return [];
        }

        const [startH, startM] = startTime.split(':').map(Number);
        const [endH, endM] = endTime.split(':').map(Number);

        for (let h = startH; h <= endH; h++) {
            const startMinute = (h === startH) ? Math.ceil(startM / 15) * 15 : 0;

            for (let m = startMinute; m < 60; m += 15) {
                if (h === endH && m > endM) break;
                if (h > endH) break;

                const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                const label = `${h.toString().padStart(2, '0')}h${m.toString().padStart(2, '0')}`;

                if (isToday) {
                    const slotDate = new Date();
                    slotDate.setHours(h, m, 0, 0);
                    if (slotDate <= now) continue;
                }

                slots.push({ time, label });
            }
        }
        return slots;
    }, [selectedMarket, pickupDate]);

    // Order deadline info
    const orderDeadlineInfo = useMemo(() => {
        if (!selectedMarket) return null;
        const deadline = getOrderDeadline(selectedMarket.market, selectedMarket, pickupDate);
        if (!deadline) return null;
        const open = new Date() < deadline;
        return { deadline, open, formatted: formatOrderDeadline(deadline) };
    }, [selectedMarket, pickupDate]);

    // Pending market change (for modal confirmation)
    const pendingMarketChangeRef = useRef<{ marketId: string; city: string; dayOfWeek: number } | null>(null);

    // Handle "Changer" button click on the sticky banner
    const handleChangeBannerClick = useCallback(() => {
        setShowMarketSelector(true);
    }, []);

    // Handle market selection from the gate/selector
    const handleSelectMarketFromGate = useCallback(async (marketId: string, city: string, dayOfWeek: number) => {
        // Check if there's an existing cart for this merchant at a different market
        if (tenantId && cart.length > 0 && selectedMarketId && selectedMarketId !== marketId) {
            // Cart conflict - show modal
            pendingMarketChangeRef.current = { marketId, city, dayOfWeek };
            setIsMarketChangeModalOpen(true);
            return;
        }

        // No conflict - update immediately
        await setGlobalMarket({ marketId, city, dayOfWeek });
        setSelectedMarketId(marketId);
        setPickupTime('');
        setShowMarketSelector(false);
    }, [tenantId, cart, selectedMarketId, setGlobalMarket]);

    // Handle market change confirmation (after modal)
    const handleConfirmMarketChange = useCallback(async () => {
        const pending = pendingMarketChangeRef.current;
        if (!pending) return;

        // Clear only this merchant's cart
        if (tenantId) {
            clearCartForMerchant(tenantId);
            setCart([]);
        }

        await setGlobalMarket({ marketId: pending.marketId, city: pending.city, dayOfWeek: pending.dayOfWeek });
        setSelectedMarketId(pending.marketId);
        setPickupTime('');
        setShowMarketSelector(false);
        setIsCheckoutOpen(false);
        pendingMarketChangeRef.current = null;
    }, [tenantId, clearCartForMerchant, setGlobalMarket]);

    // Handle market change from checkout dropdown (legacy flow for within-merchant market switch)
    const handleMarketChange = useCallback((newMarketId: string) => {
        const tm = tenantMarkets.find(m => m.marketId === newMarketId);
        if (!tm) return;

        if (cart.length > 0 && selectedMarketId && selectedMarketId !== newMarketId) {
            pendingMarketChangeRef.current = { marketId: newMarketId, city: tm.market.city, dayOfWeek: tm.market.dayOfWeek };
            setIsMarketChangeModalOpen(true);
            return;
        }

        setGlobalMarket({ marketId: newMarketId, city: tm.market.city, dayOfWeek: tm.market.dayOfWeek });
        setSelectedMarketId(newMarketId);
        setPickupTime('');
    }, [cart, selectedMarketId, tenantMarkets, setGlobalMarket]);

    const total = cart.reduce((sum, item) => {
        const product = products.find(p => p.id === item.productId);
        if (product) {
            const saleMode = item.saleMode || getEffectiveSaleMode(product);
            const basePrice = calculateEstimatedPrice(
                product,
                saleMode,
                item.quantity,
                item.servings || 0,
                item.portionSizeId || null
            );
            // Add options price modifiers (per unit for unit-based modes)
            const optionsModifier = calculateCartItemOptionsModifier(item, product);
            const optionsTotal = isUnitCountSaleMode(saleMode)
                ? optionsModifier * item.quantity
                : optionsModifier;
            if (basePrice !== null) {
                return sum + basePrice + optionsTotal;
            }
        }
        // Fallback to item.price if product not found
        if (item.price) {
            return sum + (item.price * item.quantity);
        }
        return sum;
    }, 0);

    const totalItems = cart.reduce((sum, item) =>
        // For products with portion sizes, count as units (number of portions)
        item.portionSizeId ? sum + item.quantity : ((item.saleMode === 'WEIGHT' || item.saleMode === 'WEIGHT_ESTIMATED') ? sum + 1 : sum + item.quantity), 0);

    // ==================== Handlers ====================

    const handleToggleFavorite = async () => {
        if (!params.slug) return;

        if (isFavorite) {
            await removeFavorite(params.slug);
            setIsFavorite(false);
            toast.success('Retiré des favoris');
        } else {
            await addFavorite(params.slug);
            setIsFavorite(true);
            toast.success('Ajouté aux favoris');
        }
    };

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;

        if (params.slug) {
            localStorage.setItem('pwa_tenant_slug', params.slug);
        }

        deferredPrompt.prompt();

        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
            toast.success('Installation en cours...');
        } else {
            toast.info('Installation annulée');
            localStorage.removeItem('pwa_tenant_slug');
        }

        setDeferredPrompt(null);
        setIsInstallable(false);
    };

    const optionsEqual = (opts1: OrderLineOption[], opts2: OrderLineOption[]): boolean => {
        if (opts1.length !== opts2.length) return false;
        const sorted1 = [...opts1].sort((a, b) => a.id.localeCompare(b.id));
        const sorted2 = [...opts2].sort((a, b) => a.id.localeCompare(b.id));
        return sorted1.every((opt, i) =>
            opt.id === sorted2[i].id && opt.value === sorted2[i].value
        );
    };

    const addToCart = (
        product: Product,
        quantity?: number,
        options: OrderLineOption[] = [],
        portionSizeId?: string,
        saleMode?: SaleMode,
        servings?: number
    ) => {
        const effectiveSaleMode = saleMode || getEffectiveSaleMode(product);
        const hasPortionSizes = product.portionSizes && product.portionSizes.length > 0;
        const isWeightBased = effectiveSaleMode === 'WEIGHT' || effectiveSaleMode === 'WEIGHT_ESTIMATED';
        const finalQuantity = quantity ?? (hasPortionSizes ? 1 : (isWeightBased ? 0.5 : 1));

        // Get portion size label if applicable
        const portionSize = portionSizeId
            ? product.portionSizes?.find(p => p.id === portionSizeId)
            : undefined;

        setCart(prev => {
            // Items can only be merged if they have the same productId, options, portionSizeId, AND servings
            const existing = prev.find(item =>
                item.productId === product.id &&
                optionsEqual(item.options, options) &&
                item.portionSizeId === portionSizeId &&
                item.servings === servings
            );

            if (existing) {
                return prev.map(item =>
                    item === existing
                        ? { ...item, quantity: item.quantity + finalQuantity }
                        : item
                );
            }

            return [...prev, {
                productId: product.id,
                productName: product.name,
                quantity: finalQuantity,
                price: product.price,
                options: options,
                portionSizeId: portionSizeId,
                portionSizeLabel: portionSize?.label,
                saleMode: effectiveSaleMode,
                servings: servings
            }];
        });
        toast.success(`${product.name} ajouté au panier`);
    };

    const handleAddClick = (product: Product) => {
        setSelectedProduct(product);
        setIsProductModalOpen(true);
    };

    const handleAddToCartFromModal = (
        quantity: number,
        options: OrderLineOption[],
        portionSizeId?: string,
        saleMode?: SaleMode,
        servings?: number
    ) => {
        if (selectedProduct) {
            addToCart(selectedProduct, quantity, options, portionSizeId, saleMode, servings);
        }
    };

    const updateQuantity = (index: number, delta: number) => {
        setCart(prev => {
            const newCart = [...prev];
            const item = newCart[index];
            // Determine step based on sale mode
            let step = 1;
            const saleMode = item.saleMode || 'UNIT';
            if (item.portionSizeId || saleMode === 'WEIGHT_PACKAGED') {
                step = 1; // Number of portions
            } else if (saleMode === 'WEIGHT' || saleMode === 'WEIGHT_ESTIMATED') {
                step = 0.1; // 100g increments for weight-based
            } else {
                step = 1; // Integer for UNIT and UNIT_PRICED_BY_WEIGHT
            }
            const change = delta * step;
            const newQuantity = Math.round((item.quantity + change) * 100) / 100;

            if (newQuantity <= 0) {
                return prev.filter((_, i) => i !== index);
            }

            newCart[index] = { ...item, quantity: newQuantity };
            return newCart;
        });
    };

    const toggleOption = (index: number, optionId: string) => {
        setCart(prev => {
            const newCart = [...prev];
            const item = newCart[index];
            const existingOptionIndex = item.options.findIndex(opt => opt.id === optionId);

            if (existingOptionIndex !== -1) {
                const currentValue = item.options[existingOptionIndex].value;
                if (currentValue === true) {
                    newCart[index] = {
                        ...item,
                        options: item.options.filter((_, i) => i !== existingOptionIndex)
                    };
                } else {
                    newCart[index] = {
                        ...item,
                        options: item.options.map((opt, i) =>
                            i === existingOptionIndex ? { ...opt, value: true } : opt
                        )
                    };
                }
            } else {
                newCart[index] = {
                    ...item,
                    options: [...item.options, { id: optionId, value: true }]
                };
            }
            return newCart;
        });
    };

    const scrollToSection = (section: string) => {
        const ref = sectionRefs.current[section];
        if (ref) {
            // Disable observer during manual scroll to prevent race condition
            isManualScrollingRef.current = true;
            ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Re-enable observer after scroll animation completes (~500ms)
            setTimeout(() => {
                isManualScrollingRef.current = false;
            }, 600);
        }
        setActiveSection(section);
    };

    const scrollToSubSection = (subSection: string | null) => {
        setActiveSubSection(subSection);

        // If "Tous" is selected, scroll to the section header
        if (subSection === null) {
            const sectionRef = sectionRefs.current[activeSection];
            if (sectionRef) {
                isManualScrollingRef.current = true;
                sectionRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setTimeout(() => {
                    isManualScrollingRef.current = false;
                }, 600);
            }
            return;
        }

        // Scroll to subsection group header using subSectionRefs
        const subSectionKey = `${activeSection}-${subSection}`;
        const subSectionRef = subSectionRefs.current[subSectionKey];
        if (subSectionRef) {
            isManualScrollingRef.current = true;
            subSectionRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setTimeout(() => {
                isManualScrollingRef.current = false;
            }, 600);
        }
    };

    // Pull-to-refresh handler
    const handleRefresh = useCallback(async () => {
        if (!tenantId) return;

        try {
            // Refresh products (bypasses cache)
            const freshProducts = await refreshProducts(tenantId);
            setProducts(freshProducts);

            // Refresh markets
            const freshMarkets = await getTenantMarketsWithDetails(tenantId);
            const sorted = [...freshMarkets].sort((a, b) => a.market.dayOfWeek - b.market.dayOfWeek);
            setTenantMarkets(sorted);

            toast.success('Menu actualisé');
        } catch (error) {
            console.error('[CustomerOrderPage] Pull-to-refresh error:', error);
            toast.error('Impossible de rafraîchir');
        }
    }, [tenantId]);

    // Pull-to-refresh hook
    const {
        containerRef: pullToRefreshRef,
        pullDistance,
        isRefreshing,
        isPulling,
    } = usePullToRefresh({
        onRefresh: handleRefresh,
        isEnabled: !tenantLoading && !productsLoading && !!tenantId,
    });

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Initialize Firebase functions outside try for access in catch
        const { app } = initFirebase();
        const functions = getFunctions(app, 'europe-west1');
        const checkRecentOrder = httpsCallable<
            { customerId: string; tenantId: string },
            { found: boolean; orderId: string | null }
        >(functions, 'checkRecentOrder');

        // Context for timeout verification (will be set in try block)
        let orderContext: { customerId: string; tenantId: string } | null = null;

        try {
            setSubmitting(true);
            if (!customerName.trim()) {
                toast.error('Merci d\'indiquer votre nom');
                return;
            }
            if (!pickupTime) {
                toast.error('Merci de choisir une heure de retrait');
                return;
            }
            if (cart.length === 0) {
                toast.error('Votre panier est vide');
                return;
            }

            // Check order deadline
            if (selectedMarket && !isOrderingOpen(selectedMarket.market, selectedMarket, pickupDate)) {
                toast.error('La date limite de commande est dépassée pour ce marché');
                return;
            }

            let orderTenantId = tenantId;
            if (!orderTenantId && cart.length > 0) {
                const firstProduct = products.find(p => p.id === cart[0].productId);
                orderTenantId = firstProduct?.tenantId || null;
            }

            if (!orderTenantId) {
                toast.error('Impossible de créer la commande - tenant non défini');
                return;
            }

            const [h, m] = pickupTime.split(':').map(Number);
            const [year, month, day] = pickupDate.split('-').map(Number);
            const at = new Date(year, month - 1, day, h, m, 0, 0);

            // Parallelize notification permission + customer ID (independent operations)
            const notifPromise = wantsNotifications
                ? requestNotificationPermissionWithDetails()
                : Promise.resolve(null);
            const customerIdPromise = getCustomerIdAsync();

            const [notifResult, customerId] = await Promise.all([notifPromise, customerIdPromise]);

            let fcmToken: string | undefined = undefined;
            if (notifResult) {
                if (notifResult.success) {
                    fcmToken = notifResult.token;
                } else {
                    switch (notifResult.reason) {
                        case 'ios-purged':
                            toast.error(
                                'Notifications désactivées par iOS. Supprimez l\'app de l\'écran d\'accueil et réinstallez-la.',
                                { duration: 8000 }
                            );
                            break;
                        case 'android-overlay':
                            toast.error(
                                'Permission bloquée. Fermez les bulles de chat et superpositions des autres apps, puis réessayez.',
                                { duration: 8000 }
                            );
                            break;
                        case 'denied':
                            toast.warning('Notifications refusées. Activez-les dans les réglages.');
                            break;
                        case 'not-supported':
                            toast.warning('Notifications non supportées sur ce navigateur.');
                            break;
                        default:
                            toast.warning('Impossible d\'activer les notifications.');
                    }
                }
            }

            // Store context for timeout verification
            orderContext = { customerId, tenantId: orderTenantId };

            const createOrderRateLimited = httpsCallable<
                Record<string, unknown>,
                { orderId: string }
            >(functions, 'createOrderRateLimited');

            // Add timeout to prevent indefinite waiting on network issues (15s)
            const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(() => reject(new Error('TIMEOUT')), 10000);
            });

            const result = await Promise.race([
                createOrderRateLimited({
                customerName: customerName.trim(),
                phoneNumber: phoneNumber.trim() || undefined,
                pickupAtISO: at.toISOString(),
                lines: cart.map(item => {
                    const product = products.find(p => p.id === item.productId);
                    const saleMode = item.saleMode || (product ? getEffectiveSaleMode(product) : 'UNIT');

                    // Calculate estimated values for variable price modes
                    const estimatedWeight = product
                        ? calculateEstimatedWeight(product, saleMode, item.quantity, item.servings || 0)
                        : null;
                    const baseEstimatedPrice = product
                        ? calculateEstimatedPrice(product, saleMode, item.quantity, item.servings || 0, item.portionSizeId || null)
                        : null;
                    // Include options price modifiers in estimatedPrice (per unit for unit-based modes)
                    const optionsModifier = calculateCartItemOptionsModifier(item, product);
                    const optionsTotal = isUnitCountSaleMode(saleMode)
                        ? optionsModifier * item.quantity
                        : optionsModifier;
                    const estimatedPrice = baseEstimatedPrice !== null
                        ? baseEstimatedPrice + optionsTotal
                        : null;

                    return {
                        productId: item.productId,
                        quantity: item.quantity,
                        options: item.options,
                        portionSizeId: item.portionSizeId,
                        // New sale mode fields
                        saleMode,
                        servings: item.servings,
                        estimatedWeight: estimatedWeight ?? undefined,
                        estimatedPrice: estimatedPrice ?? undefined,
                    };
                }),
                tenantId: orderTenantId,
                fcmToken,
                marketId: selectedMarketId,
                customerId,
                note: note.trim() || undefined,
            }),
                timeoutPromise,
            ]);

            // Save order locally for customer portal (fire-and-forget, non-blocking)
            if (result.data?.orderId) {
                setPlacedOrderId(result.data.orderId);
                saveLocalOrder({
                    orderId: result.data.orderId,
                    tenantId: orderTenantId,
                    tenantSlug: params.slug || '',
                    tenantName: tenantName,
                    marketId: selectedMarketId || undefined,
                    createdAt: new Date().toISOString(),
                }).catch(() => { /* IndexedDB save is best-effort */ });
            }

            // Save last market for quick access (fire-and-forget, non-blocking)
            if (selectedMarket) {
                saveLastMarket({
                    marketId: selectedMarket.marketId,
                    marketCity: selectedMarket.market.city,
                    visitedAt: new Date().toISOString(),
                }).catch(() => { /* IndexedDB save is best-effort */ });
            }

            // Clear this merchant's cart after successful order
            if (tenantId) {
                clearCartForMerchant(tenantId);
            }

            setOrderPlaced(true);
            setIsCheckoutOpen(false);
            setIsCartOpen(false);
            toast.success('Commande envoyée !');
        } catch (e) {
            console.error('Order creation error:', e);
            if (e instanceof Error && e.message === 'TIMEOUT' && orderContext) {
                // Timeout occurred - check if order was actually created
                toast.loading('Vérification de la commande...', { id: 'order-check' });
                try {
                    const checkResult = await checkRecentOrder({
                        customerId: orderContext.customerId,
                        tenantId: orderContext.tenantId,
                    });

                    if (checkResult.data?.found && checkResult.data.orderId) {
                        // Order was created! Redirect to tracking
                        toast.dismiss('order-check');
                        setPlacedOrderId(checkResult.data.orderId);
                        saveLocalOrder({
                            orderId: checkResult.data.orderId,
                            tenantId: orderContext.tenantId,
                            tenantSlug: params.slug || '',
                            tenantName: tenantName,
                            marketId: selectedMarketId || undefined,
                            createdAt: new Date().toISOString(),
                        }).catch(() => { /* best-effort */ });
                        if (selectedMarket) {
                            saveLastMarket({
                                marketId: selectedMarket.marketId,
                                marketCity: selectedMarket.market.city,
                                visitedAt: new Date().toISOString(),
                            }).catch(() => { /* best-effort */ });
                        }
                        setOrderPlaced(true);
                        setIsCheckoutOpen(false);
                        setIsCartOpen(false);
                        toast.success('Commande envoyée !');
                        return; // Exit early, don't go to finally with submitting=false
                    } else {
                        // Order not found - network issue before server received it
                        toast.error('La connexion a échoué. Veuillez réessayer.', {
                            id: 'order-check',
                            duration: 5000,
                        });
                    }
                } catch (checkError) {
                    console.error('Error checking order:', checkError);
                    toast.error(
                        'Impossible de vérifier. Votre commande a peut-être été envoyée.',
                        { id: 'order-check', duration: 8000 }
                    );
                }
            } else if (e instanceof Error && e.message === 'TIMEOUT') {
                // Timeout but no context (shouldn't happen, but handle gracefully)
                toast.error('Une erreur est survenue. Veuillez réessayer.');
            } else {
                const error = e as { code?: string; message?: string };
                if (error.code === 'functions/resource-exhausted') {
                    toast.error('Trop de commandes. Réessayez dans quelques minutes.');
                } else if (error.code === 'functions/invalid-argument') {
                    toast.error(error.message || 'Données invalides.');
                } else {
                    toast.error('Une erreur est survenue');
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== Derived: market gate logic ====================

    // Merchant is present at current market?
    const isMerchantAtCurrentMarket = useMemo(() => {
        if (!currentMarket || tenantMarkets.length === 0) return false;
        return tenantMarkets.some(tm => tm.marketId === currentMarket.marketId);
    }, [currentMarket, tenantMarkets]);

    // Should show market selection gate (no current market set)
    const needsMarketGate = !marketContextLoading && !marketsLoading && tenantMarkets.length > 0 && !currentMarket;

    // Should show incompatibility screen
    const needsIncompatibilityScreen = !marketContextLoading && !marketsLoading && tenantMarkets.length > 0
        && currentMarket && !isMerchantAtCurrentMarket && !showMarketSelector;

    // ==================== Render ====================

    // Show skeleton while tenant or products are loading
    if (tenantLoading || marketContextLoading || (tenantId && productsLoading)) {
        return <OrderPageSkeleton />;
    }

    if (tenantNotFound) {
        return (
            <TenantNotFound
                slug={params.slug}
                onBackClick={() => marketFromUrl ? navigate(`/market/${marketFromUrl}`) : navigate('/')}
            />
        );
    }

    if (tenantAbsent) {
        return (
            <MerchantAbsent
                tenantName={tenantName}
                absenceEnd={tenantAbsenceEnd}
                onBackClick={() => marketFromUrl ? navigate(`/market/${marketFromUrl}`) : navigate('/')}
            />
        );
    }

    // Market selection gate: no current market set
    if (needsMarketGate || showMarketSelector) {
        return (
            <>
            <MarketSelectionGate
                tenantMarkets={tenantMarkets}
                onSelectMarket={handleSelectMarketFromGate}
                onBackClick={() => marketFromUrl ? navigate(`/market/${marketFromUrl}`) : navigate('/')}
            />
            <MarketChangeModal
                open={isMarketChangeModalOpen}
                onOpenChange={setIsMarketChangeModalOpen}
                merchantName={tenantName}
                onConfirm={handleConfirmMarketChange}
            />
            </>
        );
    }

    // Incompatibility screen: merchant not at current market
    if (needsIncompatibilityScreen && currentMarket) {
        return (
            <MarketIncompatible
                tenantName={tenantName}
                currentMarket={currentMarket}
                compatibleMarkets={tenantMarkets}
                onSelectMarket={handleSelectMarketFromGate}
                onBackClick={() => marketFromUrl ? navigate(`/market/${marketFromUrl}`) : navigate('/')}
            />
        );
    }

    return (
        <>
        {orderPlaced && placedOrderId ? (
            <OrderTracking
                orderId={placedOrderId}
                customerName={customerName}
                pickupTime={pickupTime}
                tenantName={tenantName}
                tenantPhoneNumber={tenantPhoneNumber}
                marketId={selectedMarketId || undefined}
                cart={cart}
                products={products}
                onBackClick={() => marketFromUrl ? navigate(`/market/${marketFromUrl}`) : navigate('/')}
            />
        ) : (
        <PullToRefresh
            ref={pullToRefreshRef}
            pullDistance={pullDistance}
            isRefreshing={isRefreshing}
            isPulling={isPulling}
            className="h-full bg-gray-50"
        >
            <div className="pb-24">
                <TenantHeader
                    tenantName={tenantName}
                    slug={params.slug}
                    isFavorite={isFavorite}
                    isInstallable={isInstallable}
                    onToggleFavorite={handleToggleFavorite}
                    onInstallClick={handleInstallClick}
                    onBackClick={() => marketFromUrl ? navigate(`/market/${marketFromUrl}`) : navigate('/')}
                />

                {/* Sticky navigation group: market banner + deadline + section/subsection tabs */}
                <div className="sticky top-0 z-10">
                    {currentMarket && (
                        <CurrentMarketBanner
                            currentMarket={currentMarket}
                            onChangeClick={handleChangeBannerClick}
                        />
                    )}

                    {/* Order closure banner (replaces deadline when closure is active) */}
                    {!marketsLoading && selectedMarket && orderClosureSkipInfo ? (
                        <div className="px-4 py-3 bg-orange-50 border-b border-orange-200">
                            <div className="flex items-start gap-2">
                                <span className="text-orange-600 mt-0.5 text-sm">&#128274;</span>
                                <div>
                                    <p className="text-sm font-medium text-orange-800">
                                        Commandes fermées pour le marché de {orderClosureSkipInfo.formattedOriginalDate}
                                    </p>
                                    <p className="text-xs text-orange-700 mt-0.5">
                                        Les commandes ont fermé le {orderClosureSkipInfo.formattedDeadline}.
                                        Vous commandez pour {orderClosureSkipInfo.formattedSkippedToDate}.
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : !marketsLoading && selectedMarket && orderDeadlineInfo ? (
                        <div className={`px-4 py-2 text-xs ${orderDeadlineInfo.open ? 'text-amber-700 bg-amber-50' : 'text-red-600 font-medium bg-red-50'}`}>
                            {orderDeadlineInfo.open
                                ? `Commandez avant le ${orderDeadlineInfo.formatted}`
                                : `Commandes fermees depuis le ${orderDeadlineInfo.formatted}`
                            }
                        </div>
                    ) : null}

                    <SectionTabs
                        sections={sortedSections}
                        activeSection={activeSection}
                        onSectionClick={scrollToSection}
                        tabsContainerRef={tabsContainerRef as React.RefObject<HTMLDivElement>}
                    />

                    {/* SubSection tabs - only shown when active section has subSections */}
                    {currentSubSections.length > 0 && (
                        <SubSectionTabs
                            subSections={currentSubSections}
                            activeSubSection={activeSubSection}
                            onSubSectionClick={scrollToSubSection}
                        />
                    )}
                </div>

                {/* Products list by section */}
                <div className="px-4 py-4 space-y-8">
                {sortedSections.map((section) => (
                    <div
                        key={section}
                        ref={(el) => { sectionRefs.current[section] = el; }}
                        className="scroll-mt-20"
                    >
                        <h2 className="text-xl font-bold text-gray-900 mb-4">
                            {section.charAt(0).toUpperCase() + section.slice(1)}
                        </h2>
                        <div className="space-y-6">
                            {productsBySubSection[section]?.map((group, groupIndex) => (
                                <div
                                    key={group.subSection || `no-subsection-${groupIndex}`}
                                    ref={(el) => {
                                        if (group.subSection) {
                                            subSectionRefs.current[`${section}-${group.subSection}`] = el;
                                        }
                                    }}
                                    className={group.subSection ? 'scroll-mt-32' : ''}
                                >
                                    {group.subSection && (
                                        <h3 className="text-base font-semibold text-gray-700 mb-3 pl-1">
                                            {group.subSection.charAt(0).toUpperCase() + group.subSection.slice(1)}
                                        </h3>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {group.products.map(product => (
                                            <div
                                                key={product.id}
                                                ref={(el) => { productRefs.current[product.id] = el; }}
                                                className="scroll-mt-32"
                                            >
                                                <ProductRowCard
                                                    product={product}
                                                    onAddClick={handleAddClick}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {cart.length > 0 && (
                <FloatingCartButton
                    totalItems={totalItems}
                    total={total}
                    onClick={() => setIsCartOpen(true)}
                />
            )}
            </div>
        </PullToRefresh>
        )}

        {/* Modals rendered outside the conditional so Radix Dialog portals
            always unmount cleanly (open goes false before tree changes) */}
        <CartModal
            open={isCartOpen}
            onOpenChange={setIsCartOpen}
            cart={cart}
            products={products}
            total={total}
            onUpdateQuantity={updateQuantity}
            onToggleOption={toggleOption}
            onCheckout={() => {
                setIsCartOpen(false);
                setIsCheckoutOpen(true);
            }}
        />

        <ProductAddModal
            open={isProductModalOpen}
            product={selectedProduct}
            products={products}
            onClose={() => {
                setIsProductModalOpen(false);
                setSelectedProduct(null);
            }}
            onAddToCart={handleAddToCartFromModal}
        />

        <CheckoutModal
            open={isCheckoutOpen}
            onOpenChange={setIsCheckoutOpen}
            customerName={customerName}
            onCustomerNameChange={setCustomerName}
            phoneNumber={phoneNumber}
            onPhoneNumberChange={setPhoneNumber}
            note={note}
            onNoteChange={setNote}
            pickupTime={pickupTime}
            onPickupTimeChange={setPickupTime}
            wantsNotifications={wantsNotifications}
            onWantsNotificationsChange={setWantsNotifications}
            tenantMarkets={tenantMarkets}
            selectedMarketId={selectedMarketId}
            onMarketChange={handleMarketChange}
            marketsLoading={marketsLoading}
            selectedMarket={selectedMarket}
            formattedPickupDate={formattedPickupDate}
            timeSlots={timeSlots}
            submitting={submitting}
            onSubmit={onSubmit}
            absenceSkipInfo={absenceSkipInfo}
            orderClosureSkipInfo={orderClosureSkipInfo}
        />

        <MarketChangeModal
            open={isMarketChangeModalOpen}
            onOpenChange={setIsMarketChangeModalOpen}
            merchantName={tenantName}
            onConfirm={handleConfirmMarketChange}
        />
        </>
    );
}
